/**
 * consoleBuffer — #328
 *
 * Wraps console.error / console.warn to capture the last 30 calls
 * into a rolling ring buffer. The Feedback widget attaches the tail
 * to bug submissions so triage isn't blind ("clicked X, nothing
 * happened" → now ships with the actual stack trace).
 *
 * Sensitive patterns (JWT, AWS keys, OpenAI/Google API keys) are
 * scrubbed before storage to avoid leaking secrets via the feedback
 * channel.
 *
 * Install once from src/main.jsx. The wrapper passes calls through
 * to the real console, so existing logging is untouched.
 */

const RING_SIZE = 30;

let ring = [];
let installed = false;

// Redaction patterns. Run in order; the first match wins per substring.
// Each pattern is greedy but anchored to the secret's distinctive shape
// so we don't accidentally redact normal text.
const REDACTORS = [
  // JWT: 3 base64url segments separated by dots. Real JWTs are long
  // enough that a 10+ char minimum per segment avoids false positives
  // on dotted identifiers.
  {
    pattern: /[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g,
    label: '[REDACTED-JWT]',
  },
  // AWS access key id
  { pattern: /AKIA[0-9A-Z]{16}/g, label: '[REDACTED-AWS-KEY]' },
  // OpenAI / similar: sk-<20+ chars>
  { pattern: /sk-[A-Za-z0-9]{20,}/g, label: '[REDACTED-API-KEY]' },
  // Google: AIza<35 chars>
  { pattern: /AIza[A-Za-z0-9_-]{35}/g, label: '[REDACTED-API-KEY]' },
];

function redact(text) {
  let out = text;
  for (const { pattern, label } of REDACTORS) {
    out = out.replace(pattern, label);
  }
  return out;
}

function safeStringify(arg) {
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`;
  }
  if (typeof arg === 'string') return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    // Circular ref or non-serialisable; fall back to coerced String().
    try {
      return String(arg);
    } catch {
      return '[unserialisable]';
    }
  }
}

function pushEntry(level, args) {
  const message = redact(args.map(safeStringify).join(' '));
  ring.push({
    level,
    ts: new Date().toISOString(),
    message,
  });
  if (ring.length > RING_SIZE) {
    // Drop oldest to keep the ring bounded.
    ring.shift();
  }
}

export function installConsoleBuffer() {
  if (installed) return;
  installed = true;
  if (typeof console === 'undefined') return;

  ['error', 'warn'].forEach((level) => {
    const original = console[level]?.bind(console);
    if (!original) return;
    console[level] = (...args) => {
      try {
        pushEntry(level, args);
      } catch {
        // Buffer failure must never break the real console call.
      }
      original(...args);
    };
  });
}

export function getRecentConsoleEntries() {
  // Return a defensive copy so callers can't mutate the ring.
  return ring.slice();
}

/**
 * Format entries as a single text block — one line per entry with
 * `[LEVEL @ ts] message` prefix. Used by FeedbackService to embed
 * the tail in a bug submission.
 */
export function formatConsoleEntries(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return '';
  return entries
    .map((e) => `[${e.level.toUpperCase()} @ ${e.ts}] ${e.message}`)
    .join('\n');
}

/** Test-only reset. Resets the ring AND the installed flag so a fresh
 *  installConsoleBuffer() call re-wraps cleanly. */
export function __resetForTests() {
  ring = [];
  installed = false;
}
